import React from 'react';
import {
  ForcedCompanyFilterContext,
  useOptionalFilterState,
} from '@/hooks/useFilter';
import {
  MachinePickerFieldsFragment,
  useGetMachinesForPickerQuery,
} from '@/generated/codegen';
import { isNotNullish } from '@/utils/nullish';
import {
  ConnectedCombobox,
  ConnectedComboboxProps,
  useDebouncedComboboxSearchValue,
} from './Combobox';
import { MachineSearchNotice } from './MachineSearchNotice';

const INVISIBLE_MARKER = '\u200b'; // Zero-width space to detect the extra stuff we added to the label, that the back end's search does not know about.

export type ConnectedMachineComboboxProps = {
  filterMachines?: (machine: MachinePickerFieldsFragment) => boolean;
  /** Explicit context overrides to avoid relying on global filter provider (prevents remount focus issues) */
  forcedCompanyId?: number;
  forcedProjectId?: number;
} & Omit<
  ConnectedComboboxProps<MachinePickerFieldsFragment>,
  'items' | 'loading'
>;

export const ConnectedMachineCombobox: React.FC<ConnectedMachineComboboxProps> =
  React.memo(
    ({
      name,
      placeholder = 'Buscar equipo...',
      emptyLabel = 'No se encontraron equipos con ese nombre',
      filterMachines,
      ...props
    }) => {
      const searchValue = useDebouncedComboboxSearchValue(name);
      const filter = useOptionalFilterState();
      const { data, loading } = useGetMachinesForPickerQuery({
        variables: {
          context: props.forcedCompanyId
            ? {
                company_id: props.forcedCompanyId,
                project_id: props.forcedProjectId,
              }
            : filter.query?.company_id
              ? {
                  company_id: filter.query.company_id,
                  project_id: filter.query.project_id,
                }
              : undefined,
          filter: {
            query: searchValue.split(INVISIBLE_MARKER)[0],
          },
          pagination: { limit: 50, offset: 0 },
        },
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
      });

      const items = React.useMemo(
        () =>
          data?.getMachines && 'items' in data.getMachines
            ? data.getMachines.items
                .filter(isNotNullish)
                .filter((m) => (filterMachines ? filterMachines(m) : true))
            : [],
        [data, filterMachines]
      );
      const receivedCount =
        data?.getMachines && 'items' in data.getMachines
          ? data.getMachines.items.length
          : 0;

      const getComboboxItem = React.useCallback(
        (machine: MachinePickerFieldsFragment) => ({
          id: String(machine.id),
          label: `${machine.name}${
            machine.type?.type
              ? `${INVISIBLE_MARKER} - ${machine.type.type}`
              : ''
          }`,
          value: machine,
          keywords: [
            machine.name,
            machine.type?.type,
            machine.domain,
            machine.serial_number,
          ].filter(isNotNullish),
        }),
        []
      );

      return (
        <ConnectedCombobox
          name={name}
          loading={loading}
          items={items}
          placeholder={placeholder}
          emptyLabel={emptyLabel}
          getComboboxItem={getComboboxItem}
          resultsFooter={
            <MachineSearchNotice
              receivedCount={receivedCount}
              totalItems={
                data?.getMachines && 'items' in data.getMachines
                  ? data.getMachines.page_info?.total_items
                  : undefined
              }
              limit={50}
              loading={loading}
              active={!props.disabled}
            />
          }
          {...props}
        />
      );
    }
  );

export const ForcedContextConnectedMachineCombobox: React.FC<
  ConnectedMachineComboboxProps & { company_id?: number }
> = React.memo(({ company_id, ...props }) => (
  <ForcedCompanyFilterContext company_id={company_id}>
    <ConnectedMachineCombobox
      {...props}
      disabled={props.disabled || !company_id}
    />
  </ForcedCompanyFilterContext>
));
